import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import api from '../../utils/axios';

interface StateItem {
  id: number;
  state_name: string;
  state_code: string;
  allowed: boolean;
  country_name: string;
}

interface CountryItem {
  id: number;
  name: string;
  code2: string;
}

const StateList = () => {
  const [states, setStates] = useState<StateItem[]>([]);
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('India');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Fetch states from the API
  const fetchStates = async () => {
    try {
      const response = await api.get('/getStatelist');
      if (response.data) {
        setStates(response.data.data);
      } else {
        toast.error('Failed to fetch states.');
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Error fetching states.');
    }
  };

  // Fetch countries list from the API for the dropdown selector
  const fetchCountries = async () => {
    try {
      const response = await api.get('/getcountryList');
      if (response.data) {
        const sorted = response.data.data.sort((a: CountryItem, b: CountryItem) =>
          a.name.localeCompare(b.name)
        );
        setCountries(sorted);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchCountries();
  }, []);

  // Toggle "Allowed" status
  const toggleAllowed = async (state: StateItem) => {
    try {
      const updatedState = { ...state, allowed: !state.allowed };

      const response = await api.post(
        `/updateState/${state.id}`,
        updatedState
      );

      if (response.data.status) {
        toast.success('State updated successfully!');
        setStates((prev) =>
          prev.map((s) => (s.id === state.id ? { ...s, allowed: !s.allowed } : s))
        );
      } else {
        toast.error('Failed to update state.');
      }
    } catch (error) {
      console.error('Error updating state:', error);
      toast.error('Error updating state.');
    }
  };

  // Filter states by selected country and search term
  const filteredStates = states.filter(
    (state) =>
      state?.country_name === selectedCountry &&
      (state?.state_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state?.state_code?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">State Restrictions</h1>
        <p className="text-sm text-gray-500">
          Configure allowed and restricted states/regions for access control
        </p>
      </div>

      {/* ================= SEARCH & FILTER CARD ================= */}
      <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
        {/* Dropdown & Search Inputs */}
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Country Selector Dropdown */}
          <div className="w-full md:w-64">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Select Country</label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-50/50"
            >
              {countries.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Search States</label>
            <input
              type="text"
              placeholder={`Search by state name or code in ${selectedCountry}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-50/50"
            />
          </div>
        </div>

        {/* Display States Table */}
        <div className="overflow-x-auto border rounded-lg">
          {filteredStates.length > 0 ? (
            <table className="w-full text-sm text-left text-gray-800">
              <thead className="bg-gray-50 text-gray-600 font-semibold border-b">
                <tr>
                  <th className="px-4 py-3 w-16 text-center">S.No.</th>
                  <th className="px-4 py-3">State Name</th>
                  <th className="px-4 py-3 w-40">State Code</th>
                  <th className="px-4 py-3 w-32 text-center">Allowed</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredStates.map((state, index) => (
                  <tr key={state.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-center font-medium text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{state.state_name}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{state.state_code}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center items-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={state.allowed}
                            onChange={() => toggleAllowed(state)}
                            className="sr-only peer"
                          />
                          <div
                            className={`w-11 h-6 rounded-full relative transition-colors duration-200 ease-in-out ${
                              state.allowed ? 'bg-green-500' : 'bg-slate-300'
                            }`}
                          >
                            <div
                              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ease-in-out ${
                                state.allowed ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            ></div>
                          </div>
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-12 text-center text-gray-500">
              No states found for {selectedCountry}.
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default StateList;
